/**
 * Screen 3 — the document list.
 *
 * Status pills over a table, a selection bar for the bulk actions, a per-row
 * kebab and a numbered pager — the `Portal · DeCAs (lista)` frame.
 *
 * **One list, two document types** (design D1). A goods DeCA (Orden FOM/2861/2012) and a passenger
 * route sheet (FOM/1230/2013) are different legal instruments served by two parallel, independently
 * paginated APIs. They are shown together because a traffic manager wants one place to look, and
 * the `tipo` filter plus the «Viajeros» mark beside a route sheet's number are what keep the reader
 * from ever having to guess which one a row is. (That mark had a column of its own, «Tipo», until
 * the plate took it on 2026-09-30.) Three consequences are visible on screen rather than hidden,
 * each with its own notice under the table: `Todos` pages only as deep as the smaller server cap
 * allows (`documentRows.ts`), sorting is pinned outside a goods-only view, and two filters the
 * passenger API does not accept leave route sheets out of the list instead of listing the wrong
 * ones.
 *
 * The grouping guardrail is enforced here as well as by the server: BOE Sixth
 * permits grouping only where the contractual shipper **and** the actual carrier
 * are identical for every consignment. Any goods row can be ticked, in any estado. The
 * guardrail sits on the *action*, not on the checkbox — see the comment above
 * `groupable` — so a selection that cannot be grouped is refused when it is
 * submitted, by the server, with a per-document reason.
 *
 * There is no bulk endpoint in the contract, so *Descargar* and *Anular* are
 * loops over the per-document operations. Both report what actually happened
 * per document rather than a single "done": voiding ten documents can succeed
 * for eight, and on a legal record the two that did not must be named.
 */

import { useMemo, useState } from 'react'
import { useNavigate, useSearch } from '@tanstack/react-router'
import { useTranslation } from 'react-i18next'
import { unwrap, unwrapWithETag, ifMatch, type DecaEstado, type DecaSummary } from '@deca/api'

import { api, downloadFile, MIME } from '../app/api.ts'
import { apiErrorMessage } from '../app/errors.ts'
import { useDecaList, useInvalidateDeca, usePassengerDecaList } from '../app/queries.ts'
import { useAuth } from '../app/auth.tsx'
import { offeredFamilies } from '../deca/transportType.ts'
import { useOrganisationChoice } from '../app/useOrganisationChoice.ts'
import { mergeDocumentPages, type DocumentRow } from '../deca/documentRows.ts'
import { organisationToEnter, readsAcrossOrganisations } from '../deca/reach.ts'
import { abilitiesFor } from '../deca/roles.ts'
import { hasDeliveryConfirmation } from '../deca/delivery.ts'
import { canGroupTogether, canVoid, estadoPluralKey, isGenerated } from '../deca/estado.ts'
import { formatServiceDate } from '../deca/format.ts'
import { AppShell } from '../shell/AppShell.tsx'
import { Button, Card, Chip, EmptyState, Notice } from '../ui/primitives.tsx'
import { DataTable, Pagination, type Column } from '../ui/DataTable.tsx'
import { Dialog } from '../ui/Dialog.tsx'
import { DeliveredTag, EstadoChip, FromAppTag, GroupedTag } from '../ui/EstadoChip.tsx'
import { RowMenu } from '../ui/RowMenu.tsx'
import { TextAreaInput } from '../ui/form.tsx'
import { useToast } from '../ui/Toast.tsx'
import { useStepBackPastEnd } from '../ui/useStepBackPastEnd.ts'
import { applyOnEnter, useDateFilter } from '../ui/filterInput.ts'
import {
  decaListSearchSchema,
  documentTipoValues,
  DECA_PER_PAGE,
  PAGE_DEFAULT,
  SORT_DEFAULT,
  TIPO_DEFAULT,
  type DecaListSearch,
  type DecaSort,
} from './searchSchemas.ts'

/**
 * The pills the frame draws. `BORRADOR` and `ANULADO` are deliberately not among
 * them — but they are reachable from the Panel's status rail, so an active
 * filter outside this set is appended below rather than leaving the user on a
 * filtered list with nothing switched on and no way to see why.
 */
const PILL_ESTADOS: readonly DecaEstado[] = ['EN CURSO', 'MODIFICADO', 'FINALIZADO', 'CADUCADO']

/** The «Entrega» filter's three choices, and the `entregado` each one is (#160). */
const DELIVERY_CHOICES = [
  { value: 'all', entregado: undefined },
  { value: 'delivered', entregado: true },
  { value: 'undelivered', entregado: false },
] as const

/**
 * NIFs and plates are codes, not words: a phone keyboard should offer capitals and leave them
 * uncorrected, and label its Enter key as the search it now is.
 */
const CODE_INPUT = {
  autoCapitalize: 'characters',
  autoCorrect: 'off',
  spellCheck: false,
  enterKeyHint: 'search',
} as const

export function DecaListScreen() {
  const { t, i18n } = useTranslation()
  const navigate = useNavigate()
  const toast = useToast()
  const invalidate = useInvalidateDeca()
  const { principal } = useAuth()
  const abilities = abilitiesFor(principal)

  const rawSearch = useSearch({ strict: false })
  const search = decaListSearchSchema.parse(rawSearch)
  const page = search.page ?? PAGE_DEFAULT
  /**
   * #220: a company offered one type gets no tabs, and the list of its own type, whatever the address
   * asks for: paged by its server, and sortable when it is goods, as the tab was. Only a company that
   * already has documents of the other type gets both, merged, so that they stay listed (plan §1.2);
   * one read of a single row of the other type says whether it has any. Until that read answers, its
   * own type.
   */
  const offered = offeredFamilies(principal)
  const tabbed = offered.goods && offered.passenger
  const own = offered.goods ? 'mercancias' : 'viajeros'
  const otherGoods = useDecaList({ page: 1, perPage: 1 }, !tabbed && own === 'viajeros')
  const otherSheets = usePassengerDecaList({ page: 1, perPage: 1 }, !tabbed && own === 'mercancias')
  const hasOther = ((own === 'viajeros' ? otherGoods : otherSheets).data?.meta?.total ?? 0) > 0
  const tipo = tabbed ? (search.tipo ?? TIPO_DEFAULT) : hasOther ? TIPO_DEFAULT : own
  /** `todos`: two sources, merged here. Either other value: one source, paged by its own server. */
  const mixed = tipo === 'todos'

  /**
   * The merge reproduces the true order only on the key both sources order by
   * (`documentRows.ts`), and `listPassengerDecas` takes no `sort` parameter at all — so outside a
   * goods-only view there is nothing to offer but `createdAt`. Left live, a header click would
   * show an order that was never applied *and* slice the page out of a different population than
   * the user asked for. `list.sortPinned` says so under the table.
   */
  const sortable = tipo === 'mercancias'
  const sort = sortable ? (search.sort ?? SORT_DEFAULT) : SORT_DEFAULT

  /**
   * Filters the route-sheet list cannot be asked for, and what the screen does about them.
   *
   * `listPassengerDecas` declares `estado` as **one** `DecaEstado` where the goods list declares a
   * repeatable array, and declares no `isGrouped` at all. (The server happens to accept both — a
   * repeated `estado` and `isGrouped` — but the frozen contract does not declare them, and the
   * generated client is the contract. Widening that is a contract change with two clients to
   * regenerate, not a screen change.)
   *
   * So rather than send one of two selected statuses and quietly list the wrong route sheets, the
   * passenger source is left out entirely and `list.filterGoodsOnly` names the filters that did
   * it. A stated absence is recoverable; a plausible wrong list of legal documents is not.
   *
   * «Entrega» (#160) is the third, and the plainest case: a route sheet has no delivery
   * confirmation, so `listPassengerDecas` has no `entregado` to send.
   */
  const multiEstado = (search.estado?.length ?? 0) > 1
  const goodsOnlyFilters = [
    ...(multiEstado ? [t('list.filterEstado')] : []),
    ...(search.isGrouped ? [t('list.filterGrouped')] : []),
    ...(search.entregado !== undefined ? [t('list.filterDelivery')] : []),
  ]

  const wantsGoods = tipo !== 'viajeros'
  const readsPassenger = tipo !== 'mercancias' && goodsOnlyFilters.length === 0

  const [selected, setSelected] = useState<Set<string>>(new Set())
  const [showFilters, setShowFilters] = useState(false)
  const [voidOpen, setVoidOpen] = useState(false)
  const [busy, setBusy] = useState(false)

  const { organisation: context, choose } = useOrganisationChoice()
  const across = readsAcrossOrganisations(abilities, context)

  /**
   * Opening a row found across companies has to enter that company first, or the detail screen and
   * everything it fetches resolve against the wrong tenant and 404. `organisationToEnter` answers
   * `null` whenever nothing needs to change, which is every click a tenant user ever makes — so
   * this is one branch, not a cross-company special case bolted onto navigation.
   *
   * The second branch is the document type: a *hoja de ruta* has its own route, because it is not
   * a DeCA. Both summaries carry `organisationId`/`organisationName`, so the company step above is
   * type-neutral and only the destination differs.
   */
  function openRow(r: DocumentRow) {
    const enter = organisationToEnter(abilities, r.row, context)
    if (enter) choose(enter)
    if (r.tipo === 'mercancias') void navigate({ to: '/decas/$decaId', params: { decaId: r.row.id } })
    else void navigate({ to: '/hojas-de-ruta/$sheetId', params: { sheetId: r.row.id } })
  }

  /**
   * How many rows the merge needs from each source for this page.
   *
   * Asked of `mergeDocumentPages` rather than restated here: it is the same arithmetic that
   * decides `reachablePages`, and a second copy of it is how the fetch and the bound it is
   * reported under drift apart. For a single-type view the server pages as it always has.
   */
  const fetchSize = mixed
    ? mergeDocumentPages({
        goods: { rows: [], total: 0 },
        passenger: { rows: [], total: 0 },
        page,
        perPage: DECA_PER_PAGE,
      }).requiredPerSource
    : DECA_PER_PAGE

  const goodsParams = useMemo(
    () => ({
      // Under `todos` each source is asked for the front of its own list and the merge does the
      // paging; a single-type view is paged by the server, exactly as before.
      page: mixed ? 1 : page,
      perPage: fetchSize,
      sort,
      // Spread rather than `allOrganisations: across`. `false` would be serialised and sent, and
      // `readScopeOf` parses this **as a string** — the route declares no querystring schema, so
      // Fastify coerces nothing — which makes an explicit `false` a value the server has to
      // interpret instead of a parameter it never receives.
      ...(across ? { allOrganisations: true } : {}),
      ...(search.q ? { q: search.q } : {}),
      ...(search.estado?.length ? { estado: search.estado } : {}),
      ...(search.cargadorNif ? { cargadorNif: search.cargadorNif } : {}),
      ...(search.transportistaNif ? { transportistaNif: search.transportistaNif } : {}),
      ...(search.matricula ? { matricula: search.matricula } : {}),
      ...(search.fechaDesde ? { fechaDesde: search.fechaDesde } : {}),
      ...(search.fechaHasta ? { fechaHasta: search.fechaHasta } : {}),
      ...(search.isGrouped ? { isGrouped: true } : {}),
      // Both values are sent: `false` is «Sin entregar», a filter in its own right.
      ...(search.entregado !== undefined ? { entregado: search.entregado } : {}),
    }),
    [search, page, sort, across, mixed, fetchSize],
  )

  /**
   * The same filters, in the other API's words. The two endpoints were written for two documents
   * and share almost no parameter spelling, so this mapping is the whole of what makes one filter
   * bar mean the same thing on both halves of the list. Each line is a name the contract fixes:
   *
   *   `transportistaNif` → `transportista_nif`, `cargadorNif` → `contratante_nif`,
   *   `matricula` → `matricula_autobus`, `fechaDesde`/`fechaHasta` → `desde`/`hasta`.
   *
   * `desde`/`hasta` bound `servicio_fecha` — when the coach runs — which is the same question
   * `fechaDesde`/`fechaHasta` ask of `fecha_realizacion`, and they carry the very same values: the
   * bare day the date control picked, which the server has read as that Madrid day since #93
   * (`dayBoundParam`), or the timestamp a Panel month link put in the URL, which it uses as given.
   * The contract takes either form for both pairs.
   */
  const passengerParams = useMemo(
    () => ({
      page: mixed ? 1 : page,
      perPage: fetchSize,
      ...(across ? { allOrganisations: true } : {}),
      ...(search.q ? { q: search.q } : {}),
      // One status, never an array — see `goodsOnlyFilters`, which is what keeps a two-status
      // selection from ever reaching this call.
      ...(search.estado?.length === 1 ? { estado: search.estado[0] } : {}),
      ...(search.transportistaNif ? { transportista_nif: search.transportistaNif } : {}),
      ...(search.cargadorNif ? { contratante_nif: search.cargadorNif } : {}),
      ...(search.matricula ? { matricula_autobus: search.matricula } : {}),
      ...(search.fechaDesde ? { desde: search.fechaDesde } : {}),
      ...(search.fechaHasta ? { hasta: search.fechaHasta } : {}),
    }),
    [search, page, across, mixed, fetchSize],
  )

  const goods = useDecaList(goodsParams, wantsGoods)
  const passenger = usePassengerDecaList(passengerParams, readsPassenger)

  /**
   * The rows on this page, and the totals printed under them.
   *
   * Two shapes, deliberately, and not one. `todos` merges the two sources, which is where the
   * `createdAt` order and the paging bound come from. A single type is **not** routed through the
   * merge even though it would look like a pass-through: the server has already paged *and*
   * ordered that case, and `mergeDocumentPages` would re-sort by `createdAt` — undoing the sort
   * the user asked for, on the one view where the sort control is live — and would throw on any
   * partial last page, because the `total` it checks the fetch against is the collection's and not
   * the page's.
   */
  const view = useMemo(() => {
    if (mixed) {
      const merged = mergeDocumentPages({
        goods: {
          rows: wantsGoods ? (goods.data?.data ?? []) : [],
          total: wantsGoods ? (goods.data?.meta.total ?? 0) : 0,
        },
        passenger: {
          rows: readsPassenger ? (passenger.data?.data ?? []) : [],
          total: readsPassenger ? (passenger.data?.meta.total ?? 0) : 0,
        },
        page,
        perPage: DECA_PER_PAGE,
      })
      return {
        rows: merged.rows,
        total: merged.total,
        // The pager must not offer a page the merge cannot serve correctly.
        totalPages: Math.min(merged.totalPages, merged.reachablePages),
        reachablePages: merged.reachablePages,
        // 🚨 **Not `merged.truncated`**, which answers a different question. That field is
        // `page > reachablePages` — did *this request* ask past the bound — and the line above
        // is what makes it unaskable: the pager never offers a page beyond `reachablePages`, so
        // the only way to reach it is to hand-edit the URL. The notice would then have fired for
        // nobody, on the one screen where silence is the whole defect: 600 goods and 600 sheets
        // read as pages 1…10 under a header saying 1200 documentos, with 1100 documents
        // unreachable and nothing on screen saying why.
        //
        // The notice is about the **collection**, not the request — design D1: past the bound
        // the page control stops *and a notice asks the user to narrow the filter*. So the
        // condition is whether the collection runs past what paging can serve, which is exactly
        // what the clamp above discarded.
        truncated: merged.totalPages > merged.reachablePages,
      }
    }

    const meta = wantsGoods ? goods.data?.meta : readsPassenger ? passenger.data?.meta : undefined
    const rows: DocumentRow[] = wantsGoods
      ? (goods.data?.data ?? []).map((row) => ({ tipo: 'mercancias', row }))
      : readsPassenger
        ? (passenger.data?.data ?? []).map((row) => ({ tipo: 'viajeros', row }))
        : []
    return {
      rows,
      total: meta?.total ?? 0,
      totalPages: meta?.totalPages ?? 1,
      reachablePages: meta?.totalPages ?? 1,
      truncated: false,
    }
  }, [mixed, wantsGoods, readsPassenger, goods.data, passenger.data, page])

  /**
   * 🚨 Neither source asked — the state `wantsGoods` and `readsPassenger` can both reach.
   *
   * `Viajeros` plus two statuses is the shortest route: the type rules out the goods list, and
   * two statuses rule out the passenger one (`goodsOnlyFilters`), so nothing is fetched at all.
   * `Agrupadas` plus `Viajeros` does the same. Both are two clicks from the filter bar.
   */
  const askedSomething = wantsGoods || readsPassenger

  /**
   * Both halves have to have answered before a count over both of them is true — and one of them
   * has to have been **asked**.
   *
   * Without the first term this was `true` in the state above, by short circuit: each half reads
   * "not wanted, or answered", and a source that was never asked satisfies it for free. The
   * header then printed «0 documentos» and the table rendered "Todavía no hay documentos — cree
   * el primero", over a company whose documents were simply never requested. Those are confident
   * false statements about a set of legal documents, which is the shape this screen's filter
   * comment rules out: a stated absence is recoverable, a plausible wrong one is not.
   */
  const counted = askedSomething && (!wantsGoods || goods.isSuccess) && (!readsPassenger || passenger.isSuccess)

  /**
   * A page past the end shows the last page (#165), not «Todavía no hay documentos» and «Crear
   * documento» over a company that has documents. This view pages itself under `todos`, so the
   * bound is its own `totalPages`, the one the pager offers, not either source's; and only once both
   * halves have answered, since a count from one of them is not the list's.
   */
  useStepBackPastEnd(counted ? { page, total: view.total, totalPages: view.totalPages } : undefined, page, (last) => {
    void navigate({ to: '/decas', search: (current) => ({ ...(current as DecaListSearch), page: last }), replace: true })
  })

  const hasFilters = Boolean(
    search.q ||
      search.estado?.length ||
      search.cargadorNif ||
      search.transportistaNif ||
      search.matricula ||
      search.fechaDesde ||
      search.fechaHasta ||
      search.isGrouped ||
      search.entregado !== undefined ||
      tipo !== TIPO_DEFAULT,
  )

  // A status the frame has no pill for, but the URL is filtering on anyway.
  const extraEstados = (search.estado ?? []).filter((estado) => !PILL_ESTADOS.includes(estado))

  function updateSearch(patch: Partial<DecaListSearch>) {
    void navigate({
      to: '/decas',
      search: (current) => ({ ...(current as DecaListSearch), page: 1, ...patch }),
    })
  }

  function toggleEstado(estado: DecaEstado) {
    const current = new Set(search.estado ?? [])
    if (current.has(estado)) current.delete(estado)
    else current.add(estado)
    updateSearch({ estado: current.size ? [...current] : undefined })
  }

  // The day itself, as the date control gives it. The server reads a bare `YYYY-MM-DD` as that
  // **Madrid calendar day**, from its midnight to the next one's, each at the offset Madrid has then
  // (`dayBoundParam`), so a winter day and a summer day are both whole. Until 2026-10-10 the list
  // sent the day plus a fixed `+02:00`, which put both ends of a winter day an hour early.
  function applyFechaDesde(day: string) {
    updateSearch({ fechaDesde: day || undefined })
  }

  function applyFechaHasta(day: string) {
    updateSearch({ fechaHasta: day || undefined })
  }

  // A pick applies at once, a typed date when it is finished (ui/filterInput.ts, #111).
  const fechaDesdeFilter = useDateFilter(applyFechaDesde)
  const fechaHastaFilter = useDateFilter(applyFechaHasta)

  /**
   * Goods only, and that is what keeps `canGroupTogether` type-checking without a cast.
   *
   * Selection feeds grouping (BOE Sixth, which has no passenger equivalent), bulk void and bulk
   * download, all against `/api/v1/decas/…`. A route sheet's id sent to any of them is a 404 at
   * best. The passenger lifecycle is its own task; until then the checkbox is disabled on those
   * rows rather than silently selecting something the bar cannot act on.
   */
  const selectedRows = useMemo(
    () => view.rows.flatMap((r) => (r.tipo === 'mercancias' && selected.has(r.row.id) ? [r.row] : [])),
    [view.rows, selected],
  )

  /**
   * The guardrail sits on the *action*, not on the checkbox. Selection also
   * feeds bulk download and bulk void, which apply to documents grouping never
   * could — gating the checkbox by the narrowest of the three actions would
   * make a finalised document impossible to select and therefore impossible to
   * download. The rule itself is `canGroupTogether` (BOE Sixth).
   */
  const groupable = useMemo(() => canGroupTogether(selectedRows), [selectedRows])

  function toggle(id: string) {
    setSelected((current) => {
      const next = new Set(current)
      if (next.has(id)) next.delete(id)
      else next.add(id)
      return next
    })
  }

  async function downloadOne(row: DecaSummary) {
    await downloadFile(
      `/api/v1/decas/${encodeURIComponent(row.id)}/pdf`,
      MIME.pdf,
      `${row.documentNumber ?? 'DeCA'}.pdf`,
    )
  }

  /**
   * One request per document, in sequence. Parallel fetches would finish in a
   * burst the browser treats as an unsolicited multi-download; one at a time is
   * also the only way to say which document failed.
   */
  async function downloadSelected() {
    const targets = selectedRows.filter(isGenerated)
    const skipped = selectedRows.length - targets.length
    if (targets.length === 0) {
      toast.error(t('list.bulkNoPdf'))
      return
    }

    setBusy(true)
    let done = 0
    const failed: string[] = []
    for (const row of targets) {
      try {
        await downloadOne(row)
        done += 1
      } catch {
        failed.push(row.documentNumber ?? row.id)
      }
    }
    setBusy(false)

    if (failed.length) toast.error(t('list.bulkDownloadPartial', { done, failed: failed.join(', ') }))
    else if (skipped) toast.show(t('list.bulkDownloadSkipped', { done, skipped }), 'info')
    else toast.success(t('list.bulkDownloadDone', { count: done }))
  }

  const columns: Column<DocumentRow>[] = [
    {
      key: 'documentNumber',
      // `panel.colDeca` — the literal word "DeCA" — was this header until the table learned to
      // carry route sheets, and it then named every `Viajeros` row as the other legal instrument.
      // The neutral header the screen shipped with is right again now that the column is mixed;
      // the Panel keeps the short one, because the Panel really is goods only (see its columns).
      header: t('list.columns.documentNumber'),
      sortKey: 'documentNumber',
      numeric: true,
      render: (r) => (
        <span className="row wrap" style={{ gap: 'var(--space-sm)' }}>
          <span className="doc-number">{r.row.documentNumber ?? t('estado.BORRADOR')}</span>
          {/* Which legal instrument the row is (design D1), since the plate took the Tipo column
              (Paco, 2026-09-30). The exception is marked and the rule is not: most companies issue
              goods DeCAs only, and a «Mercancías» on every row said nothing. Here, beside the
              number, it also heads a phone's card. */}
          {r.tipo === 'viajeros' ? <Chip tone="info">{t('list.tipo.viajeros')}</Chip> : null}
          {/* Both types group, and both count what they grouped — consignments on a DeCA,
              services on a route sheet. Same tag, the number read from each one's own field. */}
          {r.row.isGrouped ? (
            <GroupedTag count={r.tipo === 'mercancias' ? r.row.consignmentCount : r.row.serviceCount} />
          ) : null}
          {/* #110: made on a linked phone, a DeCA or, since #182, a route sheet. On the actor's type,
              never its wording; the server sends `null` for an old row. */}
          {r.row.createdBy?.type === 'INSTALL' ? <FromAppTag /> : null}
        </span>
      ),
    },
    {
      // Second, beside the identifier, where the type was until Paco asked for the plate
      // (2026-09-30): the vehicle is what a traffic manager looks a document up by. The tractor on
      // a DeCA, the sheet's bus on a route sheet. Left blank rather than dashed, so a draft with no
      // plate yet has no «Matrícula» line on its phone card.
      key: 'matricula',
      header: t('list.columns.matricula'),
      render: (r) => (r.tipo === 'mercancias' ? r.row.matricula_tractor : r.row.matricula_autobus) || null,
    },
    // Only while reading across companies. For a tenant user every row is the same company, and
    // the column would be 140 px saying so on every screen. `organisationName` is **required** on
    // both summaries in the contract — chosen that way in the design precisely so this cannot
    // silently render blank the way the 2026-08-03 serialiser gap did with optional fields.
    ...(across
      ? [
          {
            key: 'organisation',
            header: t('list.columns.organisation'),
            render: (r: DocumentRow) => (
              <span className="truncate" style={{ maxWidth: 150, display: 'block' }}>
                {r.row.organisationName}
              </span>
            ),
          },
        ]
      : []),
    {
      // One column, two words. The party that contracts the transport is the *cargador* on a DeCA
      // and the *contratante* on a route sheet; they are not the same legal role, which is why the
      // header names both rather than picking one.
      key: 'counterparty',
      header: t('list.columns.counterparty'),
      render: (r) => (
        <div className="stack-sm" style={{ gap: 0 }}>
          <span className="truncate" style={{ maxWidth: 130, display: 'block' }}>
            {r.tipo === 'mercancias' ? r.row.cargador_razon_social : r.row.contratante_nombre}
          </span>
          <span className="small muted tabular">
            {r.tipo === 'mercancias' ? r.row.cargador_nif : r.row.contratante_nif}
          </span>
        </div>
      ),
    },
    {
      key: 'carrier',
      header: t('list.columns.carrier'),
      render: (r) => (
        <div className="stack-sm" style={{ gap: 0 }}>
          <span className="truncate" style={{ maxWidth: 130, display: 'block' }}>
            {r.tipo === 'mercancias' ? r.row.transportista_razon_social : r.row.transportista_nombre}
          </span>
          <span className="small muted tabular">{r.row.transportista_nif}</span>
        </div>
      ),
    },
    {
      // No branch: **both** summaries expose `origen_resumen` / `destino_resumen`, already
      // summarised by their own server ("3 orígenes" for a grouped document), so the goods
      // rendering serves both types unchanged.
      key: 'route',
      header: t('list.columns.route'),
      render: (r) => (
        <span className="truncate" style={{ display: 'block', maxWidth: 165 }}>
          {t('list.routeSummary', { origen: r.row.origen_resumen, destino: r.row.destino_resumen })}
        </span>
      ),
    },
    {
      // The field names differ; the formatter does not. Both are `date-time` in the contract and
      // both are serialised with `.toISOString()` by their route, so `formatServiceDate` is right
      // for each: it prints the hour a service has, which `formatDate` would drop, and none where
      // the flag says there is none.
      key: 'fecha',
      header: t('list.columns.fecha'),
      sortKey: 'fecha_realizacion',
      numeric: true,
      // Either may carry a calendar day with no hour: a goods document since 2026-09-18, a route
      // sheet since #102 (Art. 2.1(c) of FOM/1230/2013 names no hour either). Each by its own flag.
      render: (r) =>
        r.tipo === 'mercancias'
          ? formatServiceDate(
              r.row.fecha_realizacion,
              r.row.fechaRealizacionHasTime,
              i18n.language,
            )
          : formatServiceDate(r.row.servicio_fecha, r.row.servicioFechaHasTime, i18n.language),
    },
    {
      key: 'estado',
      header: t('list.columns.estado'),
      // In the card's heading too, after the type. Not at its end: beside the kebab, a status chip
      // leaves the number about 100 px on a 375 px phone, and `.doc-number` does not wrap.
      card: 'title',
      // #160: how far a goods DeCA is delivered, beside its estado. A route sheet has no delivery
      // confirmation, and its summary no `delivery`.
      render: (r) => (
        <span className="row wrap" style={{ gap: 'var(--space-sm)' }}>
          <EstadoChip estado={r.row.estado} voidReason={r.row.voidReason} />
          {r.tipo === 'mercancias' ? (
            <DeliveredTag delivery={r.row.delivery} consignmentCount={r.row.consignmentCount} />
          ) : null}
        </span>
      ),
    },
    {
      key: 'actions',
      header: <span className="sr-only">{t('common.actions')}</span>,
      align: 'right',
      // The kebab in the card's top-right corner, where a menu is looked for, not under the fields.
      card: 'end',
      // Goods only, for the reason `selectedRows` states: every item here posts to
      // `/api/v1/decas/{id}/…`. A route sheet's own menu arrives with its lifecycle.
      render: (r) =>
        r.tipo === 'mercancias' ? (
          <RowMenu
            label={t('list.rowActions', { documentNumber: r.row.documentNumber ?? '' })}
            items={[
              {
                key: 'open',
                label: t('list.openRow'),
                icon: 'eye',
                onSelect: () => openRow(r),
              },
              {
                key: 'pdf',
                label: t('detail.downloadPdf'),
                icon: 'download',
                // Off while reading across companies, and twice over. `fetchBinary` sends no
                // `X-Organisation-Id` with no context, so `/decas/{id}/pdf` resolves against the
                // caller's own tenant and 404s; and entering the row's company first would clear the
                // query cache underneath the user, leaving them on an empty list while a download
                // starts. One click further — open the document, download it from there — works
                // every time.
                disabled: across || !isGenerated(r.row),
                onSelect: () => {
                  void downloadOne(r.row).catch((caught) => toast.error(apiErrorMessage(caught, t)))
                },
              },
              {
                key: 'void',
                label: t('detail.void'),
                icon: 'ban',
                danger: true,
                disabled: !abilities.voidDecas || !canVoid(r.row),
                onSelect: () => {
                  setSelected(new Set([r.row.id]))
                  setVoidOpen(true)
                },
              },
            ]}
          />
        ) : null,
    },
  ]

  return (
    <AppShell title={t('list.title')}>
      <div className="filter-row">
        {/* Which document type, not which status: this picks the *source*, and the two others
            below filter within it. Three options rather than two checkboxes, because "neither"
            is not a list anyone wants. */}
        {tabbed ? (
          <div className="segmented" role="group" aria-label={t('list.filterTipo')}>
            {documentTipoValues.map((value) => (
              <button
                key={value}
                type="button"
                className="segmented__option"
                aria-pressed={tipo === value}
                onClick={() => updateSearch({ tipo: value === TIPO_DEFAULT ? undefined : value })}
              >
                {t(`list.tipo.${value}`)}
              </button>
            ))}
          </div>
        ) : null}

        <button
          type="button"
          className="pill"
          aria-pressed={!search.estado?.length}
          onClick={() => updateSearch({ estado: undefined })}
        >
          {t('list.allPill')}
        </button>

        {[...PILL_ESTADOS, ...extraEstados].map((estado) => (
          <button
            key={estado}
            type="button"
            className="pill"
            aria-pressed={search.estado?.includes(estado) ?? false}
            onClick={() => toggleEstado(estado)}
          >
            {t(estadoPluralKey(estado))}
          </button>
        ))}

        {counted ? <span className="small muted">{t('list.documentCount', { count: view.total })}</span> : null}

        <span className="spacer" />

        <Button
          size="sm"
          variant="ghost"
          icon="search"
          onClick={() => setShowFilters((value) => !value)}
          aria-expanded={showFilters}
        >
          {t('common.filter')}
        </Button>

        {hasFilters ? (
          <Button size="sm" variant="ghost" onClick={() => void navigate({ to: '/decas', search: {} })}>
            {t('common.clearFilters')}
          </Button>
        ) : null}
      </div>

      {selected.size > 0 ? (
        <div className="selection-bar">
          <span>{t('list.selectedDecas', { count: selected.size })}</span>
          {/* Terse here, as the frame has it; the full rule is the notice under
              the table, which appears exactly when a selection breaks it. */}
          <span className="small" style={{ fontWeight: 400 }}>
            · {t('list.groupNote')}
          </span>
          <span className="spacer" />
          <Button size="sm" variant="ghost" onClick={() => setSelected(new Set())} disabled={busy}>
            {t('common.clearSelection')}
          </Button>
          <Button
            size="sm"
            variant="primary"
            icon="group"
            disabled={!groupable || busy}
            onClick={() => void navigate({ to: '/decas/agrupar', search: { ids: [...selected] } })}
          >
            {t('list.groupSelected')}
          </Button>
          <Button
            size="sm"
            icon="download"
            loading={busy}
            disabled={busy}
            onClick={() => void downloadSelected()}
          >
            {t('common.download')}
          </Button>
          {abilities.voidDecas ? (
            <Button
              size="sm"
              icon="ban"
              disabled={busy || !selectedRows.some(canVoid)}
              onClick={() => setVoidOpen(true)}
            >
              {t('detail.void')}
            </Button>
          ) : null}
        </div>
      ) : null}

      <Card flush>
        {showFilters ? (
          <div className="filter-bar">
            <div className="form-grid" style={{ width: '100%' }}>
              <label className="field">
                <span className="field__label">{t('list.filterCargador')}</span>
                <input
                  className="input"
                  defaultValue={search.cargadorNif ?? ''}
                  {...CODE_INPUT}
                  onKeyDown={applyOnEnter((value) => updateSearch({ cargadorNif: value || undefined }))}
                  onBlur={(event) => updateSearch({ cargadorNif: event.currentTarget.value || undefined })}
                />
              </label>
              <label className="field">
                <span className="field__label">{t('list.filterTransportista')}</span>
                <input
                  className="input"
                  defaultValue={search.transportistaNif ?? ''}
                  {...CODE_INPUT}
                  onKeyDown={applyOnEnter((value) => updateSearch({ transportistaNif: value || undefined }))}
                  onBlur={(event) =>
                    updateSearch({ transportistaNif: event.currentTarget.value || undefined })
                  }
                />
              </label>
              <label className="field">
                <span className="field__label">{t('list.filterMatricula')}</span>
                <input
                  className="input"
                  defaultValue={search.matricula ?? ''}
                  {...CODE_INPUT}
                  onKeyDown={applyOnEnter((value) => updateSearch({ matricula: value || undefined }))}
                  onBlur={(event) => updateSearch({ matricula: event.currentTarget.value || undefined })}
                />
              </label>
              <label className="field">
                <span className="field__label">
                  {t('list.filterFecha')} · {t('common.from')}
                </span>
                <input
                  className="input"
                  type="date"
                  defaultValue={search.fechaDesde?.slice(0, 10) ?? ''}
                  {...fechaDesdeFilter}
                />
              </label>
              <label className="field">
                <span className="field__label">
                  {t('list.filterFecha')} · {t('common.to')}
                </span>
                <input
                  className="input"
                  type="date"
                  defaultValue={search.fechaHasta?.slice(0, 10) ?? ''}
                  {...fechaHastaFilter}
                />
              </label>
              <label className="field">
                <span className="field__label">{t('list.filterDelivery')}</span>
                <select
                  className="select"
                  value={DELIVERY_CHOICES.find((choice) => choice.entregado === search.entregado)?.value ?? 'all'}
                  onChange={(event) => {
                    const value = event.currentTarget.value
                    updateSearch({ entregado: DELIVERY_CHOICES.find((choice) => choice.value === value)?.entregado })
                  }}
                >
                  {DELIVERY_CHOICES.map((choice) => (
                    <option key={choice.value} value={choice.value}>
                      {t(`list.deliveryOption.${choice.value}`)}
                    </option>
                  ))}
                </select>
              </label>
              <label className="checkbox" style={{ alignSelf: 'end', paddingBottom: 10 }}>
                <input
                  type="checkbox"
                  checked={search.isGrouped ?? false}
                  onChange={(event) => updateSearch({ isGrouped: event.currentTarget.checked || undefined })}
                />
                {t('list.filterGrouped')}
              </label>
            </div>
          </div>
        ) : null}

        <DataTable
          rows={view.rows}
          columns={columns}
          // The document's own id, unprefixed, even though the key now spans two tables: this is
          // what `DataTable` hands back through `selection.onToggle`, and those ids travel on to
          // `/decas/agrupar` and to `POST /decas/{id}/void`. A `tipo:` prefix would read well here
          // and quietly break both. Both ids are UUIDs, so they cannot collide.
          rowKey={(r) => r.row.id}
          caption={t('list.title')}
          isLoading={goods.isLoading || passenger.isLoading}
          // Either half failing hides rows that exist. A half-list of legal documents that looks
          // whole is worse than an error, so one error fails the whole table.
          error={goods.error ?? passenger.error}
          onRetry={() => {
            void goods.refetch()
            void passenger.refetch()
          }}
          onRowClick={openRow}
          sort={
            sortable ? { value: sort, onChange: (next) => updateSearch({ sort: next as DecaSort }) } : undefined
          }
          selection={
            abilities.writeDecas
              ? {
                  selected,
                  onToggle: toggle,
                  onToggleAll: (ids) =>
                    setSelected((current) =>
                      ids.every((id) => current.has(id)) ? new Set() : new Set(ids),
                    ),
                  selectable: (r) => r.tipo === 'mercancias',
                }
              : undefined
          }
          empty={
            // Two empty states, because there are two reasons for an empty table and they ask
            // for opposite things. «Todavía no hay documentos … cree el primero» is a claim
            // about the company's records, and it is false whenever the filters ruled every
            // source out before a request was made — there the table is empty because nothing
            // was asked, and the way out is to drop a filter, not to create a document. The
            // `Crear documento` button goes with it: inviting a first document over a company
            // that may have hundreds is the confident-wrong half of the same sentence.
            askedSomething ? (
              <EmptyState
                icon="file"
                title={t('list.emptyTitle')}
                // A Gestor Individual reaches only their own documents (Point 7): an empty list says
                // where theirs will appear, not that the company has none.
                body={abilities.ownDocumentsOnly ? t('list.emptyBodyOwn') : t('list.emptyBody')}
                action={
                  abilities.writeDecas ? (
                    <Button variant="primary" icon="plus" onClick={() => navigate({ to: '/decas/nueva' })}>
                      {t('nav.create')}
                    </Button>
                  ) : undefined
                }
              />
            ) : (
              <EmptyState
                icon="search"
                title={t('list.emptyFilteredTitle')}
                body={t('list.emptyFilteredBody', { filters: goodsOnlyFilters.join(', ') })}
              />
            )
          }
        />

        <Pagination
          page={page}
          perPage={DECA_PER_PAGE}
          total={view.total}
          totalPages={view.totalPages}
          onPage={(next) =>
            void navigate({
              to: '/decas',
              search: (current) => ({ ...(current as DecaListSearch), page: next }),
            })
          }
        />
      </Card>

      {/* Where this view stops, said out loud rather than left for the reader to discover. */}
      {view.truncated ? (
        <Notice tone="info">
          {t(tabbed ? 'list.truncated' : 'list.truncatedSingle', { pages: view.reachablePages })}
        </Notice>
      ) : null}

      {/* Without tabs (#220) there is no «Mercancías» to point at. */}
      {!sortable ? <Notice tone="info">{t(tabbed ? 'list.sortPinned' : 'list.sortPinnedSingle')}</Notice> : null}

      {goodsOnlyFilters.length > 0 && tipo !== 'mercancias' ? (
        <Notice tone="info">{t('list.filterGoodsOnly', { filters: goodsOnlyFilters.join(', ') })}</Notice>
      ) : null}

      {selectedRows.length >= 2 && !groupable ? (
        <Notice tone="info">{t('group.rule')}</Notice>
      ) : null}

      <BulkVoidDialog
        rows={selectedRows.filter(canVoid)}
        open={voidOpen}
        onClose={() => setVoidOpen(false)}
        onDone={(voided) => {
          invalidate()
          setSelected(new Set())
          if (voided) void goods.refetch()
        }}
      />
    </AppShell>
  )
}

/**
 * Voids every selected document with one shared reason.
 *
 * Each document needs its own `If-Match`, and only `GET /decas/{id}` hands one
 * out, so this is a read followed by a write per document. A failure part-way
 * leaves the earlier documents voided — which is correct, they are independent
 * legal records — so the dialog names what did not go through instead of
 * implying the whole batch rolled back.
 */
function BulkVoidDialog({
  rows,
  open,
  onClose,
  onDone,
}: {
  rows: DecaSummary[]
  open: boolean
  onClose: () => void
  onDone: (voided: boolean) => void
}) {
  const { t } = useTranslation()
  const toast = useToast()
  const [reason, setReason] = useState('')
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)
  // #160: the ones about to be cancelled that drivers have confirmed, named, as the single dialog warns.
  const delivered = rows.filter(hasDeliveryConfirmation)

  async function submit() {
    if (reason.trim().length < 3) {
      setError(t('errors.required'))
      return
    }
    setBusy(true)
    setError(null)

    let done = 0
    const failed: string[] = []
    for (const row of rows) {
      try {
        const { etag } = await unwrapWithETag(
          api.GET('/api/v1/decas/{decaId}', { params: { path: { decaId: row.id } } }),
        )
        await unwrap(
          api.POST('/api/v1/decas/{decaId}/void', {
            params: { path: { decaId: row.id }, header: ifMatch(etag) },
            body: { reason: reason.trim() },
          }),
        )
        done += 1
      } catch {
        failed.push(row.documentNumber ?? row.id)
      }
    }

    setBusy(false)
    setReason('')
    onDone(done > 0)

    if (failed.length) toast.error(t('list.bulkVoidPartial', { done, failed: failed.join(', ') }))
    else toast.success(t('list.bulkVoidDone', { count: done }))
    onClose()
  }

  return (
    <Dialog
      open={open}
      busy={busy}
      title={t('list.bulkVoidTitle', { count: rows.length })}
      onClose={onClose}
      footer={
        <>
          <Button onClick={onClose} disabled={busy}>
            {t('common.cancel')}
          </Button>
          <Button
            variant="danger"
            icon="ban"
            loading={busy}
            disabled={busy || rows.length === 0}
            onClick={() => void submit()}
          >
            {t('modify.voidSubmit')}
          </Button>
        </>
      }
    >
      <p className="small">{t('modify.voidIntro')}</p>
      <Notice tone="warning">{t('modify.voidWarning')}</Notice>
      {delivered.length > 0 ? (
        <Notice tone="warning">
          {t('list.bulkVoidDelivered', {
            count: delivered.length,
            documents: delivered.map((row) => row.documentNumber ?? row.id).join(', '),
          })}
        </Notice>
      ) : null}
      {error ? <Notice tone="danger">{error}</Notice> : null}

      <ul className="bulk-list">
        {rows.map((row) => (
          <li key={row.id} className="doc-number">
            {row.documentNumber ?? row.id}
          </li>
        ))}
      </ul>

      <TextAreaInput
        label={t('modify.voidReason')}
        required
        rows={3}
        value={reason}
        onChange={(event) => setReason(event.target.value)}
      />
    </Dialog>
  )
}
